import React, { createContext, useContext, useState, useEffect } from 'react';

// Create Cart Context
const CartContext = createContext();

// Custom hook to access CartContext
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    // Load cart from sessionStorage
    const loadCartItems = () => {
        const savedCartItems = sessionStorage.getItem('cartItems');
        return savedCartItems ? JSON.parse(savedCartItems) : [];
    };

    const [cartItems, setCartItems] = useState(loadCartItems);
    const [successMessage, setSuccessMessage] = useState(null);

    // Persist cart changes to sessionStorage
    useEffect(() => {
        if (cartItems.length > 0) {
            sessionStorage.setItem('cartItems', JSON.stringify(cartItems));
        } else {
            sessionStorage.removeItem('cartItems');
        }
    }, [cartItems]);

    // Calculate total price for a cart item
    const calculateTotalPrice = (cartItem) => {
        let totalPrice = parseFloat(cartItem.price || cartItem.item?.price || 0);

        // Required option
        if (cartItem.selectedRequiredOption) {
            totalPrice += parseFloat(cartItem.selectedRequiredOption.option_price);
        }

        // Optional options
        if (cartItem.selectedOptionalOptions?.length) {
            cartItem.selectedOptionalOptions.forEach((option) => {
                totalPrice += parseFloat(option.option_price) * (option.quantity || 1);
            });
        }

        // Swallow options
        if (cartItem.item?.swallowOptions?.length) {
            cartItem.item.swallowOptions.forEach((option) => {
                totalPrice += parseFloat(option.option_price) * (option.quantity || 1);
            });
        }

        // Main proteins (usually included, might be zero price)
        if (cartItem.item?.mainProteins?.length) {
            cartItem.item.mainProteins.forEach((option) => {
                totalPrice += parseFloat(option.option_price || 0) * (option.quantity || 1);
            });
        }

        // Extra proteins (paid extras)
        if (cartItem.item?.extraProteins?.length) {
            cartItem.item.extraProteins.forEach((option) => {
                totalPrice += parseFloat(option.option_price) * (option.quantity || 1);
            });
        }

        return totalPrice * cartItem.quantity;
    };

    // Add item to cart
    // Generate a unique key based on item ID + options
    // Generate unique key for cart item
    const generateCartItemKey = (item) => {
        const requiredOptionId = item.selectedRequiredOption ? item.selectedRequiredOption.id : 'none';
        const optionalOptionsKey = item.selectedOptionalOptions && item.selectedOptionalOptions.length > 0
            ? item.selectedOptionalOptions
                .map(opt => `${opt.id}:${opt.quantity}`)
                .sort()
                .join('|')
            : 'none';

        return `${item.id}-${requiredOptionId}-${optionalOptionsKey}`;
    };

    const addToCart = (item) => {
        if (!item || !item.id) {
            console.error('Item or item ID is missing:', item);
            return;
        }

        const {
            quantity,
            selectedRequiredOption,
            selectedOptionalOptions = []
        } = item;

        setCartItems((prevItems) => [
            ...prevItems,
            {
                item: {
                    ...item,
                    swallowOptions: item.swallowOptions || [],
                    mainProteins: item.mainProteins || [],
                    extraProteins: item.extraProteins || [],
                },
                quantity,
                selectedRequiredOption: selectedRequiredOption ? { ...selectedRequiredOption } : null,
                selectedOptionalOptions: selectedOptionalOptions ? selectedOptionalOptions.map(opt => ({ ...opt })) : [],
                key: `${item.id}-${Date.now()}-${Math.random()}`,
                totalPrice: calculateTotalPrice({ ...item, quantity }),
            }
        ]);

        setSuccessMessage('Item successfully added to the cart!');
    };

    // Update main item quantity
    const updateCartItemQuantity = (cartItemKey, newQuantity) => {
        setCartItems((prevItems) =>
            prevItems.map((cartItem) =>
                cartItem.key === cartItemKey
                    ? {
                        ...cartItem,
                        quantity: newQuantity,
                        totalPrice: calculateTotalPrice({ ...cartItem, quantity: newQuantity })
                    }
                    : cartItem
            )
        );
    };


    // NEW: Update optional option quantity
    const updateOptionalOptionQuantity = (itemId, optionId, newQuantity) => {
        setCartItems((prevItems) =>
            prevItems.map((cartItem) => {
                if (cartItem.item.id === itemId) {
                    const updatedOptions = cartItem.selectedOptionalOptions.map((opt) => {
                        if (opt.id === optionId) {
                            return { ...opt, quantity: newQuantity };
                        }
                        return opt;
                    });
                    return {
                        ...cartItem,
                        selectedOptionalOptions: updatedOptions,
                        totalPrice: calculateTotalPrice({ ...cartItem, selectedOptionalOptions: updatedOptions })
                    };
                }
                return cartItem;
            })
        );
    };

    const removeItemFromCart = (cartItemKey) => {
        setCartItems((prevItems) => prevItems.filter((item) => item.key !== cartItemKey));
    };


    // Clear cart
    const clearCart = () => {
        setCartItems([]);
    };

    // Clear success message
    const clearSuccessMessage = () => {
        setSuccessMessage(null);
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeItemFromCart,
                updateCartItemQuantity,
                updateOptionalOptionQuantity,
                clearCart,
                successMessage,
                clearSuccessMessage,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export default CartProvider;
